import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteUserResult,
  DeleteUserOptions,
  PatchUserResult,
  PatchUserOptions,
  GetUserOptions,
  GetUserResult,
  getUser,
  DeleteUserFields,
  PatchUserFields,
  GetUserFields,
  deleteUser,
  patchUser,
} from '../fetchers/user';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetUserOptions<Fields extends GetUserFields> = GetUserOptions<Fields>;

/**
 * Get the user
 * This method returns the authenticated user.
 */

export function useGetUser<Fields extends GetUserFields>(
  getOptions: UseGetUserOptions<Fields> | (() => UseGetUserOptions<Fields> | null),
  config?: SWRConfiguration<GetUserResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/users/${options.where.userId}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getUser({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUser, {
    endpoint: '/users/:userId',
    method: 'GET',
  });
}

/**
 * Get the user
 * This method returns the authenticated user.
 */

export function useGetUserLazy<Fields extends GetUserFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetUserResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetUserOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}${serializeQuery(options)}`,
          getUser({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUserLazy, {
    endpoint: '/users/:userId',
    method: 'GET',
  });
}

type UseDeleteUserOptions<Fields extends DeleteUserFields> = DeleteUserOptions<Fields>;

/**
 * Delete the user's account
 * This method deletes the Vimeo account of the authenticated user.
 */

export function useDeleteUser<Fields extends DeleteUserFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteUserResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteUserOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}${serializeQuery(options)}`,
          deleteUser({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteUser, {
    endpoint: '/users/:userId',
    method: 'DELETE',
  });
}

type UsePatchUserOptions<Fields extends PatchUserFields> = PatchUserOptions<Fields>;

/**
 * Edit the user's account
 * This method edits the Vimeo account of the authenticated user.
 */

export function usePatchUser<Fields extends PatchUserFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchUserResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchUserOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}${serializeQuery(options)}`,
          patchUser({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchUser, {
    endpoint: '/users/:userId',
    method: 'PATCH',
  });
}
